import { useEffect, useMemo, useState } from "react";

import { Button, Modal, ModalBody, ModalFooter, SelectV2 } from "@/modules/common/components";

import { FilterFieldConfig, FilterFieldRenderer } from "./types";
import { defaultFilterFieldRenderers } from "./fieldRenderers";
import { getLeafFields, isFieldApplied } from "./fieldUtils";

interface FilterModalProps<TFilters extends Record<string, any>> {
  open: boolean;
  onOpenChange(open: boolean): void;
  title?: string;
  description?: string;
  fields: FilterFieldConfig[];
  values: Partial<TFilters>;
  onApply(patch: Partial<TFilters>): void;
  onClear(): void;
  /** Validação cross-campo do rascunho — mesma função passada ao `useFilters` */
  refine?: (values: Partial<TFilters>) => string | undefined;
  renderers?: Record<string, FilterFieldRenderer>;
}

function FilterModal<TFilters extends Record<string, any>>({
  open,
  onOpenChange,
  title = "Filtrar",
  description = "Selecione os filtros disponíveis e aplique pelo menos um para filtrar a lista.",
  fields,
  values,
  onApply,
  onClear,
  refine,
  renderers,
}: FilterModalProps<TFilters>) {
  const modalFields = useMemo(() => fields.filter((field) => field.placement !== "inline"), [fields]);
  const mergedRenderers = { ...defaultFilterFieldRenderers, ...renderers };

  const [draft, setDraft] = useState<Partial<TFilters>>(values);
  const [visibleKeys, setVisibleKeys] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    setDraft(values);
    setVisibleKeys(modalFields.filter((field) => isFieldApplied(field, values)).map((field) => field.key));
  }, [open]);

  const fieldOptions = useMemo(
    () => modalFields.map((field) => ({ value: field.key, label: field.label })).sort((a, b) => a.label.localeCompare(b.label)),
    [modalFields],
  );

  const visibleFields = useMemo(
    () => modalFields.filter((field) => visibleKeys.includes(field.key)).sort((a, b) => a.label.localeCompare(b.label)),
    [modalFields, visibleKeys],
  );

  const hasValues = Object.values(draft).some((value) => value !== undefined && value !== "");
  const error = refine?.(draft);

  const handleVisibleKeysChange = (nextKeys: string[]) => {
    const removedKeys = visibleKeys.filter((key) => !nextKeys.includes(key));
    if (removedKeys.length) {
      setDraft((prev) => {
        const next = { ...prev };
        removedKeys.forEach((key) => {
          delete next[key as keyof TFilters];
          const field = modalFields.find((f) => f.key === key);
          field?.inputs?.forEach((input) => delete next[input.key as keyof TFilters]);
        });
        return next;
      });
    }
    setVisibleKeys(nextKeys);
  };

  const handleRemoveField = (key: string) => handleVisibleKeysChange(visibleKeys.filter((k) => k !== key));

  const handleApply = () => {
    const patch = {} as Partial<TFilters>;
    for (const leaf of getLeafFields(modalFields)) {
      (patch as Record<string, unknown>)[leaf.key] = draft[leaf.key as keyof TFilters];
    }
    onApply(patch);
    onOpenChange(false);
  };

  const handleClear = () => {
    setDraft({});
    setVisibleKeys([]);
    onClear();
    onOpenChange(false);
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} title={title} description={description} className="m-0 flex h-[75vh] max-w-140 flex-col">
      <SelectV2
        mode="multiple"
        align="start"
        placeholder="Selecione filtros disponíveis"
        searchPlaceholder="Pesquisar filtros"
        options={fieldOptions}
        value={visibleKeys}
        onChange={(next) => handleVisibleKeysChange(next ?? [])}
      />

      <hr className="my-4 border-t border-ui-border" />

      <ModalBody className="flex-1 gap-5 p-2 overflow-y-auto">
        {visibleFields.length === 0 && <p className="py-6 text-center text-sm text-ui-text-muted">Selecione ao menos um filtro acima.</p>}

        {visibleFields.map((field) => {
          const Renderer = mergedRenderers[field.type];
          if (!Renderer) return null;

          return (
            <div key={field.key} className="flex w-full flex-col gap-1.5">
              <div className="flex w-full items-center justify-between">
                <span className="text-sm font-normal text-ui-text-dark">{field.label}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveField(field.key)}
                  className="text-xs font-semibold text-ui-text-muted transition-colors hover:text-red-500"
                >
                  Remover
                </button>
              </div>

              <Renderer
                field={field}
                value={draft[field.key as keyof TFilters]}
                onChange={(value) => setDraft((prev) => ({ ...prev, [field.key]: value }))}
                values={draft}
                onChangeField={(key, value) => setDraft((prev) => ({ ...prev, [key]: value }))}
              />
            </div>
          );
        })}
      </ModalBody>

      {error && hasValues && <p className="text-sm text-red-600">{error}</p>}

      <ModalFooter className="shrink-0 justify-between gap-2">
        <Button className="w-1/2" variant="outlined" label="Limpar filtros" onClick={handleClear} />
        <Button className="w-1/2" label="Aplicar filtros" onClick={handleApply} disabled={!!error || !hasValues} />
      </ModalFooter>
    </Modal>
  );
}

export default FilterModal;
